var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { nonenumerable } from 'core-decorators';
import { Maybe } from 'tsmonad';
import { Core } from '../Constants';
import ClientAccessor from './CoreMixins/ClientAccessor';
import LinkAccessor from './CoreMixins/LinkAccessor';
import { OperationFinder } from './CoreMixins/OperationFinder';
import { Operation } from './Operation';
import Resource from './Resource';
const apiDocumentation = new WeakMap();
class HydraResource extends Resource {
    constructor(actualResource, apiDoc) {
        super(actualResource);
        if (apiDoc) {
            apiDocumentation.set(this, apiDoc);
        }
    }
    get apiDocumentation() {
        return Maybe.maybe(apiDocumentation.get(this));
    }
    get operations() {
        const alcaeus = this._alcaeus;
        const getClassOperations = (getOperations) => {
            const classOperations = this.types.map((type) => getOperations(type));
            const mappedLinks = this._reverseLinks
                .map((link) => link.subject.types.map((type) => ({ type, predicate: link.predicate })));
            const flattened = [].concat.apply([], mappedLinks);
            const propertyOperations = flattened.map((link) => getOperations(link.type, link.predicate));
            const supportedOperations = Array.prototype.concat.apply([], [...classOperations, ...propertyOperations]);
            const operations = supportedOperations.reduce((map, supportedOperation) => {
                if (!map.has(supportedOperation.id)) {
                    map.set(supportedOperation.id, new Operation(supportedOperation, alcaeus, this));
                }
                return map;
            }, new Map());
            return [...operations.values()];
        };
        return this.apiDocumentation
            .map((apiDocumentation) => ({
            apiDocumentation,
            getOperations: apiDocumentation.getOperations,
        }))
            .map((arg) => arg.getOperations.bind(arg.apiDocumentation))
            .map(getClassOperations)
            .valueOr([]);
    }
    getLinks(includeMissing = false) {
        return this.getProperties()
            .filter((tuple) => tuple.supportedProperty.property.isLink)
            .filter((tuple) => tuple.objects.length > 0 || includeMissing)
            .map((tuple) => ({
            resources: tuple.objects,
            supportedProperty: tuple.supportedProperty,
        }));
    }
    getProperties() {
        const getProperties = (propertiesForType) => this.types.map(propertiesForType)
            .reduce((current, supportedProperties) => {
            const next = supportedProperties
                .filter((sp) => {
                return !current.find((tuple) => tuple.supportedProperty.property.id === sp.property.id);
            })
                .map((supportedProperty) => ({
                objects: this.getArray(supportedProperty.property.id),
                supportedProperty,
            }));
            return [...current, ...next];
        }, []);
        return this.apiDocumentation
            .map((apiDocumentation) => ({
            apiDocumentation,
            getProperties: apiDocumentation.getProperties,
        }))
            .map((arg) => arg.getProperties.bind(arg.apiDocumentation))
            .map(getProperties)
            .valueOr([]);
    }
    getCollections(filter) {
        let collections = this.getArray(Core.Vocab('collection'));
        if (filter) {
            collections = collections.filter((c) => c.manages &&
                c.manages.find((managesBlock) => managesBlock.matches(filter)));
        }
        return collections;
    }
    load() {
        if (this.isAnonymous) {
            throw new Error('Cannot load an anonymous resource (blank node)');
        }
        return this._alcaeus.loadResource(this.id);
    }
    findOperations() { throw new Error('Not implemented'); }
    findOperationsDeep() { throw new Error('Not implemented'); }
    getOperationsDeep() { throw new Error('Not implemented'); }
}
__decorate([
    nonenumerable,
    __metadata("design:type", Maybe),
    __metadata("design:paramtypes", [])
], HydraResource.prototype, "apiDocumentation", null);
__decorate([
    nonenumerable,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HydraResource.prototype, "operations", null);
__decorate([
    nonenumerable,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], HydraResource.prototype, "getLinks", null);
__decorate([
    nonenumerable,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Array)
], HydraResource.prototype, "getProperties", null);
__decorate([
    nonenumerable,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], HydraResource.prototype, "getCollections", null);
export default function generateClass(alcaeus, getIncomingLinks) {
    const clientAccessorMixin = ClientAccessor(alcaeus);
    const linkAccessorMixin = LinkAccessor(getIncomingLinks);
    return OperationFinder(clientAccessorMixin(linkAccessorMixin(HydraResource)));
}
//# sourceMappingURL=HydraResource.js.map